import { useRef, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Vector3 } from 'three'
import { useBrickStore, type CameraPreset } from '@store/brickStore'
import type { OrbitControls as OrbitControlsType } from 'three-stdlib'

/**
 * Camera preset configurations (Task 3.2.5)
 *
 * Each preset defines:
 * - position: Camera position in world space
 * - target: Point the camera looks at (OrbitControls target)
 * - label: Display name for UI
 * - key: Keyboard shortcut number (1-4)
 */
export const CAMERA_PRESETS: Record<
  CameraPreset,
  { position: [number, number, number]; target: [number, number, number]; label: string; key: string }
> = {
  isometric: {
    position: [15, 15, 15],
    target: [0, 0, 0],
    label: 'Isometric',
    key: '1',
  },
  top: {
    position: [0, 30, 0],
    target: [0, 0, 0],
    label: 'Top View',
    key: '2',
  },
  front: {
    position: [0, 5, 30],
    target: [0, 0, 0],
    label: 'Front View',
    key: '3',
  },
  side: {
    position: [30, 5, 0],
    target: [0, 0, 0],
    label: 'Side View',
    key: '4',
  },
}

/**
 * CameraController Component
 *
 * Manages smooth camera transitions between preset views using lerping.
 *
 * Features:
 * - Smooth animated transitions (300ms duration)
 * - Lerps both camera position and OrbitControls target
 * - Automatically updates when activeCameraPreset changes in store
 * - Uses useFrame for smooth per-frame interpolation
 * - Respects user interaction (only animates on preset change)
 *
 * Technical Implementation:
 * - Stores target position/target in refs to avoid re-renders
 * - Uses alpha (0-1) to track transition progress
 * - Lerps using Vector3.lerp() for smooth interpolation
 * - Updates OrbitControls target for proper rotation pivot
 *
 * @component
 */
export const CameraController: React.FC = () => {
  const { camera, controls } = useThree()
  const activeCameraPreset = useBrickStore((state) => state.activeCameraPreset)

  // Animation state refs (avoid re-renders)
  const targetPosition = useRef(new Vector3())
  const targetTarget = useRef(new Vector3())
  const startPosition = useRef(new Vector3())
  const startTarget = useRef(new Vector3())
  const animating = useRef(false)
  const alpha = useRef(0)

  // Animation duration in milliseconds
  const ANIMATION_DURATION = 300

  /**
   * Trigger camera transition when preset changes
   */
  useEffect(() => {
    const preset = CAMERA_PRESETS[activeCameraPreset]
    if (!preset) return

    // Store current position/target as animation start
    startPosition.current.copy(camera.position)

    // Get current controls target (if OrbitControls exists)
    const orbitControls = controls as OrbitControlsType | undefined
    if (orbitControls && orbitControls.target) {
      startTarget.current.copy(orbitControls.target)
    } else {
      startTarget.current.set(0, 0, 0)
    }

    // Set target position/target
    targetPosition.current.set(...preset.position)
    targetTarget.current.set(...preset.target)

    // Start animation
    animating.current = true
    alpha.current = 0
  }, [activeCameraPreset, camera, controls])

  /**
   * Animate camera position and target using lerping
   * Runs every frame via useFrame
   */
  useFrame((_state, delta) => {
    if (!animating.current) return

    // Increment alpha based on delta time
    alpha.current += (delta * 1000) / ANIMATION_DURATION

    if (alpha.current >= 1) {
      // Animation complete - snap to final position
      camera.position.copy(targetPosition.current)

      const orbitControls = controls as OrbitControlsType | undefined
      if (orbitControls && orbitControls.target) {
        orbitControls.target.copy(targetTarget.current)
      }

      animating.current = false
      alpha.current = 1
    } else {
      // Interpolate camera position
      camera.position.lerpVectors(
        startPosition.current,
        targetPosition.current,
        alpha.current
      )

      // Interpolate OrbitControls target
      const orbitControls = controls as OrbitControlsType | undefined
      if (orbitControls && orbitControls.target) {
        orbitControls.target.lerpVectors(
          startTarget.current,
          targetTarget.current,
          alpha.current
        )
      }
    }
  })

  // This component doesn't render anything - it just controls camera
  return null
}
